import { RecipeDifficulty } from "../models/recipe";
import { DietaryTag } from "../models/dietary";
import { ParsedRecipe } from "../models/parsed-recipe";
import { parseIngredientLineWithSubstitute, matchIngredientGroup } from "./parseIngredientLine";
import { normalizePastedText } from "./textNormalize";

type Section = "none" | "ingredients" | "method" | "notes";

// Fold to lowercase and strip Spanish accents so section/metadata keywords match
// with or without diacritics ("Preparación" / "preparacion"). A manual map (not
// String.normalize) keeps it Hermes-safe.
const foldKey = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[áàäâ]/g, "a")
    .replace(/[éèëê]/g, "e")
    .replace(/[íìïî]/g, "i")
    .replace(/[óòöô]/g, "o")
    .replace(/[úùüû]/g, "u")
    .replace(/ñ/g, "n")
    .trim();

// Section header aliases (extend freely). Matched after folding (lowercase +
// accent-strip) and removing leading #'s / a trailing colon. Includes Spanish so
// pasted Spanish recipes segment correctly.
const SECTION_ALIASES: Record<Exclude<Section, "none">, string[]> = {
  ingredients: ["ingredients", "you'll need", "you will need", "ingredientes"],
  method: [
    "method",
    "instructions",
    "directions",
    "steps",
    "preparation",
    "preparacion",
    "elaboracion",
    "metodo",
    "instrucciones",
    "pasos",
    "modo de preparacion",
  ],
  notes: ["notes", "notas"],
};

// Metadata key aliases → canonical key. Folded like sections; Spanish included.
const METADATA_ALIASES: Record<string, string[]> = {
  serves: ["serves", "servings", "raciones", "porciones", "comensales"],
  time: ["time", "duration", "tiempo", "duracion"],
  difficulty: ["difficulty", "dificultad"],
  source: ["source", "fuente"],
};

const stripHeading = (line: string) => line.replace(/^#+\s*/, "").trim();
const stripBullet = (line: string) => line.replace(/^[-*•–]\s*/, "").trim();
const stripStepMarker = (line: string) =>
  line.replace(/^(\d+[.)]|[-*•–])\s*/, "").trim();

// Lines that are recipe-site photo placeholders, never content.
const isImageLine = (line: string) => /^image$/i.test(line);
// A line beginning with a quantity/fraction reads as an ingredient, not a title.
const looksLikeIngredient = (line: string) => /^[\d¼½¾⅓⅔]/.test(line);

function matchSection(line: string): Exclude<Section, "none"> | null {
  const normalized = foldKey(stripHeading(line).replace(/:$/, ""));
  for (const key of Object.keys(SECTION_ALIASES) as Exclude<Section, "none">[]) {
    if (SECTION_ALIASES[key].includes(normalized)) return key;
  }
  return null;
}

// "Key: value" metadata. The key is captured loosely (bounded length) then
// validated against METADATA_ALIASES, so only real keys match — a stray "Note:"
// or "Step 1:" line is left alone.
function matchMetadata(line: string): { key: string; value: string } | null {
  const m = line.match(/^([^:\n]{1,20}):\s*(.+)$/);
  if (!m) return null;
  const key = foldKey(m[1]);
  for (const canon of Object.keys(METADATA_ALIASES)) {
    if (METADATA_ALIASES[canon].includes(key)) return { key: canon, value: m[2].trim() };
  }
  return null;
}

function parseDurationToSeconds(value: string): number | undefined {
  const hours = value.match(/(\d+)\s*(h|hr|hrs|hour|hours)\b/i);
  const mins = value.match(/(\d+)\s*(m|min|mins|minute|minutes)\b/i);
  let seconds = 0;
  if (hours) seconds += parseInt(hours[1], 10) * 3600;
  if (mins) seconds += parseInt(mins[1], 10) * 60;
  if (!hours && !mins) {
    const bare = value.match(/(\d+)/);
    if (bare) seconds = parseInt(bare[1], 10) * 60; // bare number → minutes
  }
  return seconds > 0 ? seconds : undefined;
}

function applyMetadata(recipe: ParsedRecipe, key: string, value: string): void {
  switch (key) {
    case "serves":
    case "servings": {
      const n = parseInt(value, 10);
      if (!isNaN(n)) recipe.servings = n;
      break;
    }
    case "time":
    case "duration":
      recipe.cookingDuration = parseDurationToSeconds(value);
      break;
    case "difficulty": {
      // Spanish difficulty words → the stored enum (Easy/Medium/Hard).
      const spanish: Record<string, RecipeDifficulty> = {
        facil: RecipeDifficulty.Easy,
        media: RecipeDifficulty.Medium,
        medio: RecipeDifficulty.Medium,
        dificil: RecipeDifficulty.Hard,
      };
      const match =
        Object.values(RecipeDifficulty).find(
          (d) => d.toLowerCase() === value.toLowerCase(),
        ) ?? spanish[foldKey(value)];
      if (match) recipe.difficulty = match;
      break;
    }
    case "source":
      recipe.sourceUrl = value;
      break;
  }
}

// Marketing/nutritional badges printed above the title on magazine recipes.
// Dietary ones map to a DietaryTag; the rest (High fibre, Low fat…) are labels we
// drop so they aren't mistaken for the title. Matched on the WHOLE folded line, so
// only a standalone badge line is affected — an ingredient like "vegan cheese"
// (badge word plus more) never matches.
const DIETARY_BADGES: Record<string, DietaryTag> = {
  vegetarian: DietaryTag.Vegetarian,
  vegan: DietaryTag.Vegan,
  "gluten-free": DietaryTag.GlutenFree,
  "gluten free": DietaryTag.GlutenFree,
  "dairy-free": DietaryTag.DairyFree,
  "dairy free": DietaryTag.DairyFree,
  "egg-free": DietaryTag.EggFree,
  "egg free": DietaryTag.EggFree,
  "nut-free": DietaryTag.NutFree,
  "nut free": DietaryTag.NutFree,
  keto: DietaryTag.Keto,
};
const NON_DIETARY_BADGES = new Set([
  "high fibre",
  "high fiber",
  "high protein",
  "source of protein",
  "low fat",
  "low carb",
  "low sugar",
  "low kilojoule",
  "low calorie",
  "low joule",
  "healthy",
  "heart friendly",
]);

// A dietary/nutritional badge line → its tag, "skip" for a non-dietary badge we
// drop, or null when the line isn't a badge at all.
function matchDietaryBadge(line: string): DietaryTag | "skip" | null {
  const key = foldKey(stripHeading(line).replace(/:$/, ""));
  if (key in DIETARY_BADGES) return DIETARY_BADGES[key];
  if (NON_DIETARY_BADGES.has(key)) return "skip";
  return null;
}

function addDietary(recipe: ParsedRecipe, tag: DietaryTag): void {
  if (!recipe.dietary) recipe.dietary = [];
  if (!recipe.dietary.includes(tag)) recipe.dietary.push(tag);
}

// Magazine-style inline metadata with no colon, e.g. "SERVES 4", "Makes 12", or a
// combined "PREP 10 mins COOK 15 mins SERVES 4" (all on one line). Complements
// matchMetadata, which only handles "Key: value". Returns the fields found (prep +
// cook summed into one duration), or null when the line carries none. A leading
// keyword must be immediately followed by the number ("cook 15 mins"), so a method
// sentence like "Cook for 15 mins" — with a word in between — never matches.
function matchPrintedMeta(
  line: string,
): { servings?: number; durationSeconds?: number } | null {
  const folded = line.toLowerCase();
  let matched = false;
  const out: { servings?: number; durationSeconds?: number } = {};

  const serves = folded.match(/\b(?:serves|servings|makes|feeds)\s+(\d{1,3})\b/);
  if (serves) {
    out.servings = parseInt(serves[1], 10);
    matched = true;
  }

  let seconds = 0;
  const timeRe =
    /\b(?:prep|cook|cooking|total|ready in)\s*[:-]?\s*((?:\d+\s*(?:h|hr|hrs|hour|hours|m|min|mins|minute|minutes)\s*)+)/g;
  let tm: RegExpExecArray | null;
  while ((tm = timeRe.exec(folded)) !== null) {
    const s = parseDurationToSeconds(tm[1]);
    if (s) {
      seconds += s;
      matched = true;
    }
  }
  if (seconds > 0) out.durationSeconds = seconds;

  return matched ? out : null;
}

// A group header: [Crust] | ends with ":" | starts with "For ".
// Shared with the bulk editor so both accept the same heading forms
// (# Sauce · [Sauce] · Sauce: · For the sauce).
const matchGroup = matchIngredientGroup;

// Printed recipes (and the OCR of them) wrap long ingredient/instruction lines to
// the column width, so a single item spills across several physical lines. A
// wrapped continuation almost always begins mid-phrase — i.e. with a LOWERCASE
// letter — whereas a genuinely new ingredient or step starts with a quantity or a
// capitalised word. So fold any lowercase-leading line back into the line above.
// Never fold into a group/section header (that would corrupt the heading), and
// never fold the first line. Best-effort: false merges are rare (e.g. "a pinch of
// salt" on its own line) and the user reviews the text before saving.
const startsLowercase = (line: string) =>
  /^[a-záàäâéèëêíìïîóòöôúùüûñ]/.test(line);

function reflowWrapped(lines: string[]): string[] {
  const out: string[] = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const prev = out[out.length - 1];
    if (
      prev &&
      startsLowercase(line) &&
      matchGroup(prev) === null &&
      matchSection(prev) === null
    ) {
      out[out.length - 1] = `${prev} ${line}`.replace(/\s+/g, " ").trim();
    } else {
      out.push(line);
    }
  }
  return out;
}

// Turn a block of method lines into steps. Handles two real-world shapes:
//  - numbers delimit steps, with the instruction on following line(s):
//      "1" / "Rinse the pluck" / "image" / "2" / "Place in a pot"
//  - one step per line (numbered inline or plain):
//      "1. Cook pasta" / "Sauté garlic"
// `image` placeholder lines are dropped.
function collectMethodSteps(lines: string[]): string[] {
  const clean = lines.map((l) => l.trim()).filter((l) => l && !isImageLine(l));
  // Numbered steps come in three shapes: a bare "2" on its own line, an inline
  // "2. …", or the magazine "2 Cook…" (number-space-Capital, no full stop). Any
  // of them means the numbers delimit steps and following lines are that step's
  // continuation, so accumulate. Without them, it's one step per line.
  const hasStepNumbers =
    clean.some((l) => /^\d{1,2}[.)]?$/.test(l)) ||
    clean.some((l) => /^\d{1,2}\s+[A-Z]/.test(l));

  if (!hasStepNumbers) {
    return clean
      .map((l) => l.replace(/^(\d{1,2}[.)]|[-*•–])\s+/, "").trim())
      .filter(Boolean);
  }

  const steps: string[] = [];
  let current: string[] = [];
  let lastNum = 0;
  const flush = () => {
    const text = current.join(" ").trim();
    if (text) steps.push(text);
    current = [];
  };
  for (const line of clean) {
    const bare = line.match(/^(\d{1,2})[.)]?$/); // "2" / "2." / "2)"
    const dotted = line.match(/^(\d{1,2})[.)]\s+(.*)$/); // "2. text"
    const spaced = line.match(/^(\d{1,2})\s+(.*)$/); // "2 text"
    if (bare) {
      lastNum = parseInt(bare[1], 10);
      flush(); // bare step number → start a new step
    } else if (dotted) {
      lastNum = parseInt(dotted[1], 10);
      flush();
      current.push(dotted[2]);
    } else if (spaced && parseInt(spaced[1], 10) === lastNum + 1) {
      // "12 Serve…" — number+space+text with no punctuation. Only treat as a
      // step marker when it's the next expected number, so an instruction that
      // happens to start with a digit isn't split.
      lastNum = parseInt(spaced[1], 10);
      flush();
      current.push(spaced[2]);
    } else {
      current.push(line); // continuation / instruction line
    }
  }
  flush();
  return steps;
}

// Parse a block of ingredient lines (title / metadata / groups / ingredients),
// used when there are no section headers.
function parseIngredientRegion(recipe: ParsedRecipe, lines: string[]): void {
  for (const line of reflowWrapped(lines)) {
    if (!line || isImageLine(line)) continue;
    const badge = matchDietaryBadge(line);
    if (badge) {
      if (badge !== "skip") addDietary(recipe, badge);
      continue;
    }
    const printed = matchPrintedMeta(line);
    if (printed) {
      if (printed.servings != null) recipe.servings = printed.servings;
      if (printed.durationSeconds != null) recipe.cookingDuration = printed.durationSeconds;
      continue;
    }
    const meta = matchMetadata(line);
    const group = matchGroup(line);
    // First non-ingredient, non-group, non-meta line is the title.
    if (!recipe.title && !meta && group === null && !looksLikeIngredient(line)) {
      recipe.title = stripHeading(line);
      continue;
    }
    if (meta) {
      applyMetadata(recipe, meta.key, meta.value);
      continue;
    }
    if (group !== null) {
      recipe.ingredients.push({ name: group, group: true });
      continue;
    }
    recipe.ingredients.push(parseIngredientLineWithSubstitute(stripBullet(line)));
  }
}

// Parse pasted recipe text into a ParsedRecipe per the quick-paste spec.
export function parseRecipeText(input: string): ParsedRecipe {
  const recipe: ParsedRecipe = { title: "", ingredients: [], methodSteps: [] };
  // Clean Word/web paste artifacts (nbsp, zero-width chars, smart quotes, CRLF)
  // before parsing — so both the structure detection and the stored text are tidy.
  const lines = normalizePastedText(input).split(/\r?\n/);
  const sawSection = lines.some((l) => l.trim() && matchSection(l.trim()));

  // ── Sectioned parse ──
  if (sawSection) {
    let section: Section = "none";
    const ingredientLines: string[] = [];
    const methodLines: string[] = [];
    const notes: string[] = [];

    for (const raw of lines) {
      const line = raw.trim();
      if (!line) continue;

      const sec = matchSection(line);
      if (sec) {
        section = sec;
        continue;
      }
      if (section === "method") {
        methodLines.push(line);
        continue;
      }

      // Badges / colon-less "SERVES 4"-style metadata sit above the title, in the
      // pre-section region — pull them out so they don't become the title.
      if (section === "none") {
        const badge = matchDietaryBadge(line);
        if (badge) {
          if (badge !== "skip") addDietary(recipe, badge);
          continue;
        }
        const printed = matchPrintedMeta(line);
        if (printed) {
          if (printed.servings != null) recipe.servings = printed.servings;
          if (printed.durationSeconds != null) recipe.cookingDuration = printed.durationSeconds;
          continue;
        }
      }

      const meta = matchMetadata(line);
      if (meta && section !== "ingredients") {
        applyMetadata(recipe, meta.key, meta.value);
        continue;
      }

      if (section === "ingredients") {
        if (isImageLine(line)) continue;
        // Collect now; reflow wrapped lines and classify below.
        ingredientLines.push(line);
      } else if (section === "notes") {
        notes.push(line);
      } else if (!recipe.title) {
        recipe.title = stripHeading(line);
      } else {
        notes.push(line);
      }
    }

    for (const line of reflowWrapped(ingredientLines)) {
      const group = matchGroup(line);
      if (group !== null) recipe.ingredients.push({ name: group, group: true });
      else recipe.ingredients.push(parseIngredientLineWithSubstitute(stripBullet(line)));
    }
    recipe.methodSteps = collectMethodSteps(reflowWrapped(methodLines));
    if (notes.length) recipe.notes = notes.join("\n");
    return recipe;
  }

  // ── No headers → heuristic fallback ──
  const trimmed = lines.map((l) => l.trim());
  // Split ingredients from method at the first step marker. Prefer the
  // unambiguous forms — a bare "1"/"1."/"1)" or an inline "1. …"/"1) …".
  let methodStart = trimmed.findIndex(
    (l) => /^\d{1,2}[.)]?$/.test(l) || /^\d{1,2}[.)]\s+/.test(l),
  );

  // Only if none exist, fall back to the magazine "1 Cook…" form (number, space,
  // Capital letter — many printed recipes drop the full stop). This is riskier —
  // an ingredient like "1 Spanish onion" wears the same shape — so we accept it
  // only as the longest run of consecutively-numbered openers (1,2,3,…), which a
  // stray capitalised ingredient (a run of one) can't win, and require at least
  // two steps before trusting it.
  if (methodStart < 0) {
    const openers: { i: number; n: number }[] = [];
    trimmed.forEach((l, i) => {
      const m = l.match(/^(\d{1,2})\s+[A-Z]/);
      if (m) openers.push({ i, n: parseInt(m[1], 10) });
    });
    let bestLen = 1; // require a run of ≥2 (steps 1 and 2) to trust this form
    for (let k = 0; k < openers.length; k++) {
      if (openers[k].n !== 1) continue;
      let len = 1;
      while (k + len < openers.length && openers[k + len].n === len + 1) len++;
      if (len > bestLen) {
        bestLen = len;
        methodStart = openers[k].i;
      }
    }
  }

  if (methodStart > 0) {
    parseIngredientRegion(recipe, trimmed.slice(0, methodStart));
    recipe.methodSteps = collectMethodSteps(reflowWrapped(trimmed.slice(methodStart)));
  } else {
    // Nothing that looks like a step list — bullets → ingredients, else notes.
    const notes: string[] = [];
    for (const line of trimmed) {
      if (!line || isImageLine(line)) continue;
      const badge = matchDietaryBadge(line);
      if (badge) {
        if (badge !== "skip") addDietary(recipe, badge);
        continue;
      }
      const printed = matchPrintedMeta(line);
      if (printed) {
        if (printed.servings != null) recipe.servings = printed.servings;
        if (printed.durationSeconds != null) recipe.cookingDuration = printed.durationSeconds;
        continue;
      }
      const meta = matchMetadata(line);
      const group = matchGroup(line);
      if (!recipe.title && !meta && group === null && !looksLikeIngredient(line)) {
        recipe.title = stripHeading(line);
      } else if (meta) {
        applyMetadata(recipe, meta.key, meta.value);
      } else if (group !== null) {
        recipe.ingredients.push({ name: group, group: true });
      } else if (/^[-*•–]\s+/.test(line)) {
        recipe.ingredients.push(parseIngredientLineWithSubstitute(stripBullet(line)));
      } else if (/^\d{1,2}[.)]\s+/.test(line)) {
        recipe.methodSteps.push(stripStepMarker(line));
      } else {
        notes.push(line);
      }
    }
    if (notes.length) recipe.notes = notes.join("\n");
  }

  recipe.lowConfidence = true;
  return recipe;
}
